import React, { useRef, useLayoutEffect, useState } from 'react';

interface MapTooltipProps {
	x: number;
	y: number;
	iconSize: number;
	children: React.ReactNode;
}

const EDGE_PADDING = 8;
const ARROW_SIZE = 10;
const ARROW_MARGIN = 12;

const MapTooltip: React.FC<MapTooltipProps> = ({ x, y, iconSize, children }) => {
	const tooltipRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{
		left: number;
		top: number;
		arrowLeft: number;
		arrowSide: 'top' | 'bottom';
		ready: boolean;
	}>({
		left: x,
		top: y + iconSize / 2 + EDGE_PADDING,
		arrowLeft: 0,
		arrowSide: 'top',
		ready: false,
	});

	useLayoutEffect(() => {
		if (!tooltipRef.current) return;

		const rect = tooltipRef.current.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const tooltipW = rect.width;
		const tooltipH = rect.height;
		const gap = iconSize / 2 + EDGE_PADDING;

		let tooltipLeft = x - tooltipW / 2;
		let tooltipTop = y + gap;
		let arrowSide: 'top' | 'bottom' = 'top';

		if (tooltipTop + tooltipH > vh - EDGE_PADDING) {
			tooltipTop = y - gap - tooltipH;
			arrowSide = 'bottom';
		}

		if (tooltipTop < EDGE_PADDING) {
			tooltipTop = EDGE_PADDING;
		}

		if (tooltipLeft + tooltipW > vw - EDGE_PADDING) {
			tooltipLeft = vw - EDGE_PADDING - tooltipW;
		}

		if (tooltipLeft < EDGE_PADDING) {
			tooltipLeft = EDGE_PADDING;
		}

		const arrowLeft = Math.max(ARROW_MARGIN, Math.min(tooltipW - ARROW_MARGIN, x - tooltipLeft));

		setPosition({ left: tooltipLeft, top: tooltipTop, arrowLeft, arrowSide, ready: true });
	}, [x, y, iconSize]);

	const arrowClass =
		position.arrowSide === 'top'
			? 'absolute -top-[10px] w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-white'
			: 'absolute -bottom-[10px] w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-white';

	return (
		<div
			ref={tooltipRef}
			className="fixed z-[9999] pointer-events-none"
			style={{
				left: position.left,
				top: position.top,
				visibility: position.ready ? 'visible' : 'hidden',
			}}
		>
			<div
				className={arrowClass}
				style={{ left: position.arrowLeft, transform: 'translateX(-50%)' }}
			/>
			{children}
		</div>
	);
};

export default MapTooltip;
